/**
 * Workflow Progress Component
 * Visual progress indicator for the 6-stage creator workflow
 * With data-based stage locking system
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkflowStage, StageProgress, CourseDraft } from '../../types/creator-workflow';
import {
  checkStageAccess,
  getMissingRequirements,
  getStageRequirements,
} from '../../utils/stageAccessControl';
import './WorkflowProgress.css';

interface WorkflowProgressProps {
  currentStage: WorkflowStage;
  stageProgress: StageProgress;
  draft: CourseDraft; // Full draft object for data-based access control
  onStageClick?: (stage: WorkflowStage) => void;
}

const WorkflowProgress: React.FC<WorkflowProgressProps> = ({
  currentStage,
  stageProgress,
  draft,
  onStageClick,
}) => {
  const { t } = useTranslation('creator');
  const [hoveredStage, setHoveredStage] = useState<WorkflowStage | null>(null);
  const stageRequirements = getStageRequirements(draft);

  const getCompletedStagesCount = () => {
    return stageRequirements.filter((s) => isStageCompleted(s.stage)).length;
  };

  const getStageStatus = (stage: WorkflowStage): 'completed' | 'active' | 'locked' | 'pending' => {
    const isCompleted = isStageCompleted(stage);
    const isCurrentStage = stage === currentStage;
    const isAccessible = checkStageAccess(stage, draft);

    if (isCompleted) {
      return 'completed';
    } else if (isCurrentStage) {
      return 'active';
    } else if (!isAccessible) {
      return 'locked';
    } else {
      return 'pending';
    }
  };

  const isStageCompleted = (stage: WorkflowStage): boolean => {
    const stageKey = stage.toLowerCase() as keyof StageProgress;
    return stageProgress[stageKey]?.completed === true;
  };

  const isStageClickable = (stage: WorkflowStage): boolean => {
    // Can click on stages that are accessible (have required data)
    return checkStageAccess(stage, draft);
  };

  const handleStageClick = (stage: WorkflowStage) => {
    if (isStageClickable(stage) && onStageClick) {
      onStageClick(stage);
    }
  };

  return (
    <div className="workflow-progress">
      <div className="stages-container">
        {/* Background progress line */}
        <div className="stages-progress-line">
          <div
            className="stages-progress-fill"
            style={{ width: `${(getCompletedStagesCount() / stageRequirements.length) * 100}%` }}
          />
        </div>

        {stageRequirements.map((stageReq, index) => {
          const status = getStageStatus(stageReq.stage);
          const isCompleted = isStageCompleted(stageReq.stage);
          const clickable = isStageClickable(stageReq.stage);
          const missingReqs = getMissingRequirements(stageReq.stage, draft);
          const isLocked = status === 'locked';

          return (
            <div
              key={stageReq.stage}
              className={`stage-wrapper ${isCompleted ? 'step-completed' : ''}`}
              onMouseEnter={() => setHoveredStage(stageReq.stage)}
              onMouseLeave={() => setHoveredStage(null)}
            >
              <div
                className={`stage-item ${status} ${clickable ? 'clickable' : 'disabled'}`}
                onClick={() => handleStageClick(stageReq.stage)}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={(e) => {
                  if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleStageClick(stageReq.stage);
                  }
                }}
                title={isLocked ? `${t('workflow.workflowProgress.stageLocked')}: ${missingReqs.join(', ')}` : ''}
              >
                <div className="stage-icon">
                  {isCompleted ? '✓' : index + 1}
                </div>
                <div className="stage-label">{t(stageReq.labelKey)}</div>
              </div>

              {/* Tooltip for locked stages */}
              {isLocked && hoveredStage === stageReq.stage && missingReqs.length > 0 && (
                <div className="stage-tooltip">
                  <div className="tooltip-header">{t('workflow.workflowProgress.stageLocked')}</div>
                  <div className="tooltip-content">
                    <p>{t('workflow.workflowProgress.missingRequirements')}</p>
                    <ul>
                      {missingReqs.map((req, i) => (
                        <li key={i}>{req}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowProgress;
